import { ValueFormatterParams } from "ag-grid-community";
import { get } from "get-wild";
import { omit, startCase } from "lodash";
import React from "react";
import { ExperiencePurchaseItem, FieldResponse, FieldType, LineItemType, Report } from "types";
import { ProductRenderer } from "../ColumnTypes";
import { CustomProductFilter } from "../CustomFilters/CustomProductFilter";
import { CustomReport } from "../CustomReport";
import { ReportFilterOperator, ReportFilterOperatorCopy } from "../ReportFilterState";
import { ReportColumnDef, ReportColumnFilterValueType } from "../SuggestedReports";
import { formatFieldLabel, isAddonFieldResponse } from "@/models/Form/Field";

export const QuestionnaireReport = ({ report }: { report: Report }) => {
    const columns = React.useMemo(
        () =>
            getColumnDefs({
                report,
            }),
        [report],
    );

    return <CustomReport dateFields={["createdAt", "arrivalDate"]} report={report} columns={columns} unwind={unwind} />;
};

const unwind = (data: ExperiencePurchaseItem[]) => {
    const unwoundData = [];

    for (const item of data) {
        if (Array.isArray(item.guests) && item.guests.length > 0) {
            for (const [index, guest] of item.guests.entries()) {
                const formResponse =
                    index === 0 && item.formResponse
                        ? {
                              ...item.formResponse,
                              fields: [...(item.formResponse.fields ?? []), ...(guest.formResponse?.fields ?? [])],
                          }
                        : guest.formResponse;

                if (formResponse) {
                    unwoundData.push({ ...omit(item, "guests"), guest: { ...guest, formResponse } });
                }
            }
        }
    }

    return unwoundData;
};

type GetColumnDefsParams = {
    report: Report;
};
const getColumnDefs = ({ report }: GetColumnDefsParams): ReportColumnDef[] => [
    {
        field: "purchase.id",
        headerName: "Purchase ID",
        type: ["objectid", "purchase"],
    },
    {
        field: "id",
        headerName: "Reservation ID",
        type: ["objectid", "purchase"],
    },
    {
        field: "product",
        headerName: "Product",
        type: "product",
        filter: CustomProductFilter,
        filterParams: {
            report,
        },
        cellRenderer: ProductRenderer,
    },
    {
        field: "createdAt",
        headerName: "Created Date",
        type: "datetime",
        filterValueType: ReportColumnFilterValueType.DATE,
        filterParams: {
            report,
        },
    },
    {
        field: "arrivalDate",
        headerName: "Arrival Date",
        type: "date",
        filterValueType: ReportColumnFilterValueType.DATE,
        filterParams: {
            report,
        },
    },
    {
        field: "arrivalTime",
        headerName: "Arrival Time",
        filter: "agNumberColumnFilter",
        type: "arrivalTime",
        filterParams: {
            maxNumConditions: 1,
        },
    },
    {
        field: "organizer.name",
        headerName: "Organizer Name",
        minWidth: 200,
        filter: "agTextColumnFilter",
        filterParams: {
            maxNumConditions: 1,
            filterOptions: [
                {
                    displayKey: ReportFilterOperator.EQ,
                    displayName: ReportFilterOperatorCopy[ReportFilterOperator.EQ],
                    predicate: () => true,
                },
            ],
        },
    },
    {
        field: "organizer.email",
        headerName: "Organizer Email",
        minWidth: 200,
        filter: "agTextColumnFilter",
        filterParams: {
            maxNumConditions: 1,
            filterOptions: [
                {
                    displayKey: ReportFilterOperator.EQ,
                    displayName: ReportFilterOperatorCopy[ReportFilterOperator.EQ],
                    predicate: () => true,
                },
            ],
        },
    },
    {
        field: "amount",
        headerName: "Total Value",
        type: "currency",
        filterParams: {
            maxNumConditions: 1,
        },
    },
    {
        field: "guest.guestStatus",
        headerName: "Guest Status",
        minWidth: 100,
        valueFormatter: (params: ValueFormatterParams) => startCase(params.value),
        expand: {
            key: "guest.formResponse.fields",
            type: "string",
            minWidth: 130,
            headerGetter: (fieldResponses: FieldResponse[]) => {
                return fieldResponses
                    .filter((f) => (isAddonFieldResponse(f) ? Number.parseInt(f.value) > 0 : true))
                    .map((fieldResponse) => formatFieldLabel(fieldResponse));
            },
            cellRenderer: ({ data, colDef }) => {
                const fields: FieldResponse[] = get(data, "guest.formResponse.fields.*")?.filter(Boolean) ?? [];
                const field = fields.find((f) =>
                    isAddonFieldResponse(f) ? formatFieldLabel(f) === colDef.headerName : f.label === colDef.headerName,
                );
                if (!field?.value) {
                    return null;
                }

                if (field.type === FieldType.PICKUP) {
                    return field.pickupLocation?.name ?? field.value;
                }

                return field.value;
            },
            cellRendererParams: { lineItemType: LineItemType.DEMOGRAPHIC },
        },
    },
    {
        field: "shortCode",
        headerName: "Confirmation ID",
        maxWidth: 150,
    },
    {
        field: "purchase.tags",
        headerName: "Tags",
        type: "tags",
        filterValueType: ReportColumnFilterValueType.STRING,
        filter: "agSetColumnFilter",
    },
];
